/**
 * ExampleFeedbackManager Component
 * Manages the feedback example corpus for style extraction.
 */
import React, { useState } from 'react';
import type { FeedbackExample } from '../../types/memory';

interface ExampleFeedbackManagerProps {
  examples: FeedbackExample[];
  onAddExample: (text: string, category: string) => void;
  onRemoveExample: (index: number) => void;
  onReExtract: () => void;
  isLoading?: boolean;
}

const MAX_EXAMPLES = 50;

const CATEGORIES = [
  'general',
  'clarity',
  'structure',
  'citations',
  'grammar',
  'argumentation',
  'methodology',
  'analysis',
];

const ExampleFeedbackManager: React.FC<ExampleFeedbackManagerProps> = ({
  examples,
  onAddExample,
  onRemoveExample,
  onReExtract,
  isLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState('general');

  const handleAdd = () => {
    if (newText.trim()) {
      onAddExample(newText.trim(), newCategory);
      setNewText('');
      setNewCategory('general');
      setIsAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setNewText('');
      setIsAdding(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-gray-900">
            Feedback Examples
          </h3>
          <p className="text-xs text-gray-500">
            {examples.length}/{MAX_EXAMPLES} examples
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            {isExpanded ? 'Collapse' : 'View All'}
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setIsAdding(true)}
          disabled={examples.length >= MAX_EXAMPLES || isLoading}
          className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          Add Example
        </button>
        <button
          onClick={onReExtract}
          disabled={examples.length < 3 || isLoading}
          className="px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          {isLoading ? (
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                clipRule="evenodd"
              />
            </svg>
          )}
          Re-analyze Style
        </button>
      </div>

      {/* Add example form */}
      {isAdding && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <textarea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter a feedback example you typically give students..."
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            autoFocus
          />
          <div className="mt-2 flex items-center justify-between">
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setNewText('');
                  setIsAdding(false);
                }}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={!newText.trim()}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Examples list (collapsed view - show count only) */}
      {!isExpanded && examples.length > 0 && (
        <p className="text-sm text-gray-500">
          {examples.length} feedback example{examples.length !== 1 ? 's' : ''}{' '}
          stored. Click "View All" to see and manage them.
        </p>
      )}

      {/* Examples list (expanded view) */}
      {isExpanded && (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {examples.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">
              No examples yet. Add some feedback examples to help the AI learn
              your style.
            </p>
          ) : (
            examples.map((example, index) => (
              <div
                key={index}
                className="p-3 bg-gray-50 rounded-lg border border-gray-200 group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 line-clamp-2">
                      "{typeof example === 'string' ? example : example.text}"
                    </p>
                    {typeof example !== 'string' && example.category && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                        {example.category}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => onRemoveExample(index)}
                    className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove example"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Help text */}
      {examples.length < 3 && (
        <p className="mt-3 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">
          Add at least 3 feedback examples to enable style extraction.
        </p>
      )}
    </div>
  );
};

export default ExampleFeedbackManager;
